import type { CardPreviewDensity } from "@/components/shapes/card-previews";
import { PreviewBody } from "@/components/shapes/shared/card-preview-parts";
import { OPENED_PART } from "@/components/shapes/shared/object-frame";
import { cn } from "@/lib/cn";
import type { EventFields } from "./event-content";

/**
 * An event, the card and its opened window: a calendar leaf (the month on
 * the date accent, the day big below), then its title, time and place. The
 * card joins the detail to the time; opened, the full detail follows.
 */
export function EventFace({
  fields,
  density,
  opened = false,
  testIdPrefix,
}: {
  fields: EventFields;
  density: CardPreviewDensity;
  opened?: boolean;
  /** `event-card` or `event-frame`: the prefix of the parts' test ids. */
  testIdPrefix: string;
}) {
  const { title, month, day, detail, time, place } = fields;
  const when = opened ? time : [time, detail].filter(Boolean).join(" · ");
  return (
    <PreviewBody
      density={density}
      cap="h-[15em]"
      className={cn("justify-between gap-[0.9em]", opened && OPENED_PART)}
    >
      {(month || day) && (
        <div
          className="w-[4.6em] shrink-0 overflow-hidden rounded-[0.9em] bg-card-line text-center"
          data-testid={`${testIdPrefix}-date`}
        >
          <p className="bg-[var(--gadget-date)] py-[0.15em] text-[0.78em] font-semibold uppercase text-[color:var(--gadget-ink)]">
            {month || " "}
          </p>
          <p className="py-[0.2em] font-display text-[2.2em] font-medium leading-none text-card-ink">
            {day}
          </p>
        </div>
      )}
      <div className="min-w-0" data-testid={`${testIdPrefix}-text`}>
        <p className="line-clamp-2 font-display text-[1.2em] font-medium leading-[1.25] text-card-ink">
          {title}
        </p>
        {when && (
          <p className="mt-[0.2em] truncate text-[0.86em] text-card-body">
            {when}
          </p>
        )}
        {place && (
          <p className="truncate text-[0.86em] text-card-muted">{place}</p>
        )}
        {opened && detail && (
          <p className="mt-[0.4em] whitespace-pre-line text-[0.86em] text-card-body">
            {detail}
          </p>
        )}
      </div>
    </PreviewBody>
  );
}
